/* eslint-disable react/prop-types */
/*
  ResumeRenderer — pixel-true, print-true.
  - Renders at exact mm dimensions with pt-based typography.
  - Optional Hero banner (gradient / solid / accent_bar / minimal) with company logo slot.
  - Single-column mode when left_ratio >= 0.98 (hides sidebar).
  - Page-break-inside: avoid on each item → no mid-bullet splits.
  - Manual page break after an experience (experience.page_break_after).
  - Page margins handled by Print.jsx @page rules; inner padding stays proportional.
  - Optional inline editing — pass `editable={true}` + `onTextEdit(path,value)`
    to make key text fields content-editable directly inside the preview.
*/
import { useEffect, useRef } from "react";
import { Mail, Phone, MapPin, Linkedin, Globe } from "lucide-react";

/* Inline-editable text node — preserves caret while typing.
   - When `editable` is false: renders plain text (zero overhead).
   - When `editable` is true:  contentEditable + onBlur commit. Uses an
     uncontrolled ref so React never reconciles the child text mid-edit
     (which would yank the caret).
*/
function Ed({ value, path, multiline = false, onTextEdit, editable, tag: Tag = "span", style, ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!editable) return;
    const el = ref.current;
    if (!el) return;
    // Only sync DOM text when this element is NOT being edited.
    if (document.activeElement !== el) {
      const v = value == null ? "" : String(value);
      if (el.innerText !== v) el.innerText = v;
    }
  }, [value, editable]);
  if (!editable) {
    return <Tag style={style}>{value}</Tag>;
  }
  const commit = (e) => {
    const text = (e.currentTarget.innerText || "").replace(/\u00A0/g, " ");
    onTextEdit?.(path, multiline ? text : text.replace(/\n+/g, " ").trim());
  };
  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={ariaLabel || path}
      onBlur={commit}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
        if (e.key === "Escape") { e.preventDefault(); e.currentTarget.blur(); }
      }}
      onMouseDown={(e) => e.stopPropagation()}
      data-inline-edit={path}
      style={{ ...style, outline: "none", cursor: "text", minWidth: 8, borderRadius: 2 }}
    />
  );
}

const PROF_LEVEL = { Native: 5, Fluent: 5, Professional: 4, Conversational: 3, Basic: 2 };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const PAGE_DIMS_MM = {
  A4: { w: 210, h: 297 },
  Letter: { w: 215.9, h: 279.4 },
  Legal: { w: 215.9, h: 355.6 },
};

const DEFAULT_STYLE = {
  accent_color: "#1E5BFF",
  sidebar_bg: "#F4F6FA",
  sidebar_text: "#0F172A",
  page_bg: "#FFFFFF",
  text_color: "#0F172A",
  muted_color: "#64748B",
  header_style: "underline",
  left_ratio: 0.66,
  page_size: "A4",
  custom_width_mm: 210,
  custom_height_mm: 297,
  density: "normal",
  font_scale: 1.0,
  base_pt: 10,
  page_margin_mm: 12,
  hero_enabled: false,
  hero_style: "gradient",
  hero_bg_color: "#0B2A6B",
  hero_logo_url: "",
  hero_company_name: "",
  timeline: true,
};

const DENSITY_PAD_MM = { compact: 6, normal: 8, spacious: 10 };
const DENSITY_ITEM_GAP_MM = { compact: 2.2, normal: 3.0, spacious: 3.8 };

const AVOID_BREAK = { pageBreakInside: "avoid", breakInside: "avoid" };

export default function ResumeRenderer({ data, style, editable = false, onTextEdit }) {
  const s = { ...DEFAULT_STYLE, ...(style || {}) };
  const basePt = Number(s.base_pt) > 0
    ? Number(s.base_pt)
    : 10 * (Number(s.font_scale) || 1);
  const pt = (mult) => `${(basePt * mult).toFixed(2)}pt`;

  const d = data || {};
  const c = d.contact || {};
  const v = d.visibility || {};
  const accent = s.accent_color;
  const heroEnabled = !!s.hero_enabled;
  const heroStyle = s.hero_style || "gradient";
  const singleColumn = Number(s.left_ratio) >= 0.98;

  const dims = s.page_size === "Custom"
    ? { w: Number(s.custom_width_mm) || 210, h: Number(s.custom_height_mm) || 297 }
    : PAGE_DIMS_MM[s.page_size] || PAGE_DIMS_MM.A4;

  // Page margin drives EVERY-page equal padding in print. We render the paper
  // at the CONTENT width (page - 2×margin) so text wrapping is identical
  // in preview and PDF. The PreviewCanvas frames it with whitespace.
  const marginMm = Math.max(0, Number(s.page_margin_mm ?? 12));
  const contentW = Math.max(60, dims.w - 2 * marginMm);
  const contentH = Math.max(80, dims.h - 2 * marginMm);

  const padMm = DENSITY_PAD_MM[s.density] || DENSITY_PAD_MM.normal;
  const itemGapMm = DENSITY_ITEM_GAP_MM[s.density] || DENSITY_ITEM_GAP_MM.normal;
  const showTimeline = !!s.timeline && !singleColumn;
  const visible = (k) => v[k] !== false;
  const leftPct = singleColumn ? "100%" : `${Math.round(s.left_ratio * 100)}%`;
  const rightPct = singleColumn ? "0%" : `${100 - Math.round(s.left_ratio * 100)}%`;

  const heroLogo = s.hero_logo_url ? (s.hero_logo_url.startsWith("http") ? s.hero_logo_url : `${BACKEND_URL}${s.hero_logo_url}`) : "";
  const candidatePhoto = d.photo_url ? (d.photo_url.startsWith("http") ? d.photo_url : `${BACKEND_URL}${d.photo_url}`) : "";
  const showPhotoInHero = !!s.show_candidate_photo && !!candidatePhoto;

  return (
    <div
      className="paper"
      data-testid="resume-preview"
      data-page-margin-mm={marginMm}
      style={{
        width: `${contentW}mm`,
        minHeight: `${contentH}mm`,
        background: s.page_bg,
        color: s.text_color,
        fontFamily: `"${s.font_body || "Inter"}", "Inter", sans-serif`,
        fontSize: pt(1),
        lineHeight: 1.42,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {heroEnabled && (
        <HeroBanner
          style={heroStyle}
          bg={s.hero_bg_color || "#0B2A6B"}
          accent={accent}
          logo={heroLogo}
          photo={showPhotoInHero ? candidatePhoto : ""}
          company={s.hero_company_name}
          name={d.name || "Your Name"}
          title={d.title}
          contact={c}
          pt={pt}
          fontHeading={s.font_heading || "Manrope"}
          editable={editable}
          onTextEdit={onTextEdit}
        />
      )}

      <div style={{ display: "flex", minHeight: heroEnabled ? 0 : "auto" }}>
        {/* LEFT / MAIN */}
        <div
          style={{
            width: leftPct,
            padding: `${padMm}mm`,
            color: s.text_color,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          {!heroEnabled && (
            <Header
              name={d.name || "Your Name"}
              title={d.title}
              contact={c}
              pt={pt}
              accent={accent}
              muted={s.muted_color}
              textColor={s.text_color}
              fontHeading={s.font_heading || "Manrope"}
              editable={editable}
              onTextEdit={onTextEdit}
            />
          )}

          {visible("summary") && (d.summary || editable) && (
            <Section title="Professional Summary" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <Ed
                tag="p"
                path="summary"
                value={d.summary}
                multiline
                editable={editable}
                onTextEdit={onTextEdit}
                style={{ color: s.text_color, fontSize: pt(0.98), margin: 0, whiteSpace: "pre-wrap" }}
              />
              {d.section_breaks?.summary && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("experiences") && d.experiences?.length > 0 && (
            <Section title="Professional Experience" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: `${itemGapMm}mm` }}>
                {showTimeline && (
                  <div style={{ position: "absolute", top: "1mm", bottom: "1mm", left: "1.2mm", width: "0.4mm", background: accent + "55" }} />
                )}
                {d.experiences.map((e, i) => (
                  <div key={i}>
                    <div style={{ position: "relative", paddingLeft: showTimeline ? "5mm" : 0, ...AVOID_BREAK }}>
                      {showTimeline && (
                        <span style={{ position: "absolute", left: "0.3mm", top: "1.6mm", width: "2mm", height: "2mm", borderRadius: "999px", background: accent, boxShadow: "0 0 0 1.2mm #fff" }} />
                      )}
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                        <Ed tag="div" path={`experiences.${i}.role`} value={e.role} editable={editable} onTextEdit={onTextEdit}
                            style={{ fontWeight: 700, color: s.text_color, fontSize: pt(1.15) }} />
                        <div style={{ fontStyle: "italic", color: s.muted_color, fontSize: pt(0.85), flexShrink: 0 }}>
                          {[e.start_date, e.end_date].filter(Boolean).join(" – ")}
                        </div>
                      </div>
                      <Ed tag="div" path={`experiences.${i}.company`} value={[e.company, e.location].filter(Boolean).join("  ·  ")} editable={editable} onTextEdit={onTextEdit}
                          style={{ color: s.muted_color, fontSize: pt(0.92) }} />
                      <ul style={{ marginTop: "1.5mm", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.8mm" }}>
                        {(e.bullets || []).map((b, j) => (
                          <div key={j}>
                            <li style={{ display: "flex", gap: "1.6mm", color: s.text_color, fontSize: pt(0.97), lineHeight: 1.4, ...AVOID_BREAK }}>
                              <span style={{ flexShrink: 0, marginTop: "1.4mm", width: "1mm", height: "1mm", borderRadius: "999px", background: accent }} />
                              <Ed tag="span" path={`experiences.${i}.bullets.${j}`} value={b} multiline editable={editable} onTextEdit={onTextEdit} style={{ flex: 1 }} />
                            </li>
                            {d.bullet_breaks?.[`exp-${i}-${j}`] && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                          </div>
                        ))}
                      </ul>
                    </div>
                    {e.page_break_after && (
                      <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />
                    )}
                  </div>
                ))}
              </div>
              {d.section_breaks?.experiences && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("earlier_roles") && d.earlier_roles?.length > 0 && (
            <Section title="Earlier Roles" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1.4mm" }}>
                {d.earlier_roles.map((r, i) => (
                  <div key={i}>
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm", fontSize: pt(0.97), ...AVOID_BREAK }}>
                      <div>
                        <span style={{ fontWeight: 700, color: s.text_color }}>{r.role}</span>
                        {r.company && <span style={{ color: s.muted_color }}>  ·  {r.company}</span>}
                      </div>
                      <div style={{ fontStyle: "italic", color: s.muted_color, fontSize: pt(0.85), flexShrink: 0 }}>{r.duration}</div>
                    </div>
                    {r.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                  </div>
                ))}
              </div>
              {d.section_breaks?.earlier_roles && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("education") && d.education?.length > 0 && (
            <Section title="Education" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ display: "flex", flexDirection: "column", gap: "2mm" }}>
                {d.education.map((e, i) => (
                  <div key={i}>
                    <div style={AVOID_BREAK}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                        <div style={{ fontWeight: 700, color: s.text_color, fontSize: pt(1.08) }}>{e.degree}</div>
                        <div style={{ fontStyle: "italic", color: s.muted_color, fontSize: pt(0.85) }}>{e.duration}</div>
                      </div>
                      <div style={{ color: s.muted_color, fontSize: pt(0.92) }}>
                        {[e.institution, e.location].filter(Boolean).join("  ·  ")}
                      </div>
                      {e.details && <div style={{ marginTop: "0.6mm", color: s.text_color, fontSize: pt(0.97) }}>{e.details}</div>}
                    </div>
                    {e.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                  </div>
                ))}
              </div>
              {d.section_breaks?.education && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("projects") && d.projects?.length > 0 && (
            <Section title="Projects" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ display: "flex", flexDirection: "column", gap: `${itemGapMm}mm` }}>
                {d.projects.map((p, i) => (
                  <div key={i}>
                    <div style={AVOID_BREAK}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                        <div style={{ fontWeight: 700, color: s.text_color, fontSize: pt(1.1) }}>{p.name}</div>
                        <div style={{ fontStyle: "italic", color: s.muted_color, fontSize: pt(0.85) }}>{p.duration}</div>
                      </div>
                      {(p.role || p.tech) && (
                        <div style={{ color: s.muted_color, fontSize: pt(0.92) }}>
                          {[p.role, p.tech].filter(Boolean).join("  ·  ")}
                        </div>
                      )}
                      {p.description && <div style={{ marginTop: "0.6mm", color: s.text_color, fontSize: pt(0.97) }}>{p.description}</div>}
                      {(p.bullets || []).length > 0 && (
                        <ul style={{ marginTop: "1mm", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.6mm" }}>
                          {p.bullets.map((b, j) => (
                            <li key={j} style={{ display: "flex", gap: "1.6mm", color: s.text_color, fontSize: pt(0.95), lineHeight: 1.4, ...AVOID_BREAK }}>
                              <span style={{ flexShrink: 0, marginTop: "1.4mm", width: "1mm", height: "1mm", borderRadius: "999px", background: accent }} />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {p.link && <div style={{ marginTop: "0.6mm", fontSize: pt(0.85), color: accent }}>{p.link}</div>}
                    </div>
                    {p.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                  </div>
                ))}
              </div>
              {d.section_breaks?.projects && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("awards") && d.awards?.length > 0 && (
            <Section title="Awards & Recognition" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ display: "flex", flexDirection: "column", gap: "2mm" }}>
                {d.awards.map((a, i) => (
                  <div key={i}>
                    <div style={AVOID_BREAK}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                        <div style={{ fontWeight: 700, color: s.text_color, fontSize: pt(1.05) }}>{a.title}</div>
                        <div style={{ fontStyle: "italic", color: s.muted_color, fontSize: pt(0.85) }}>{a.year}</div>
                      </div>
                      {a.issuer && <div style={{ color: s.muted_color, fontSize: pt(0.92) }}>{a.issuer}</div>}
                      {a.description && <div style={{ marginTop: "0.4mm", color: s.text_color, fontSize: pt(0.95) }}>{a.description}</div>}
                    </div>
                    {a.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                  </div>
                ))}
              </div>
              {d.section_breaks?.awards && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("publications") && d.publications?.length > 0 && (
            <Section title="Publications" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ display: "flex", flexDirection: "column", gap: "2mm" }}>
                {d.publications.map((p, i) => (
                  <div key={i}>
                    <div style={AVOID_BREAK}>
                      <div style={{ fontWeight: 700, color: s.text_color, fontSize: pt(1.0) }}>{p.title}</div>
                      <div style={{ color: s.muted_color, fontSize: pt(0.9) }}>
                        {[p.venue, p.year].filter(Boolean).join("  ·  ")}
                      </div>
                      {p.description && <div style={{ marginTop: "0.4mm", color: s.text_color, fontSize: pt(0.95) }}>{p.description}</div>}
                    </div>
                    {p.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                  </div>
                ))}
              </div>
              {d.section_breaks?.publications && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {visible("volunteer") && d.volunteer?.length > 0 && (
            <Section title="Volunteer Experience" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
              <div style={{ display: "flex", flexDirection: "column", gap: "2mm" }}>
                {d.volunteer.map((v2, i) => (
                  <div key={i}>
                    <div style={AVOID_BREAK}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                        <div style={{ fontWeight: 700, color: s.text_color, fontSize: pt(1.05) }}>{v2.role}</div>
                        <div style={{ fontStyle: "italic", color: s.muted_color, fontSize: pt(0.85) }}>{v2.duration}</div>
                      </div>
                      <div style={{ color: s.muted_color, fontSize: pt(0.92) }}>{v2.organization}</div>
                      {v2.description && <div style={{ marginTop: "0.4mm", color: s.text_color, fontSize: pt(0.95) }}>{v2.description}</div>}
                    </div>
                    {v2.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
                  </div>
                ))}
              </div>
              {d.section_breaks?.volunteer && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
            </Section>
          )}

          {/* Custom user-defined sections — LEFT column */}
          {(d.custom_sections || []).filter((cs) => cs.visible !== false && cs.column === "left").map((cs) => (
            <CustomSectionRender key={cs.id} cs={cs} accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading} textColor={s.text_color} muted={s.muted_color} />
          ))}

          {/* When single-column, fold sidebar content into the main column too */}
          {singleColumn && <SidebarBlocks s={s} d={d} pt={pt} visible={visible} accent={accent} />}
        </div>

        {/* RIGHT (sidebar) */}
        {!singleColumn && (
          <div
            style={{
              width: rightPct,
              padding: `${padMm}mm`,
              background: s.sidebar_bg,
              color: s.sidebar_text,
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            <SidebarBlocks s={s} d={d} pt={pt} visible={visible} accent={accent} />
          </div>
        )}
      </div>
    </div>
  );
}

function Header({ name, title, contact, pt, accent, muted, textColor, fontHeading, editable, onTextEdit }) {
  const c = contact || {};
  return (
    <div>
      <Ed
        tag="h1"
        path="name"
        value={name}
        editable={editable}
        onTextEdit={onTextEdit}
        style={{
          fontFamily: `"${fontHeading}", sans-serif`,
          fontSize: pt(2.4), lineHeight: 1.06, letterSpacing: "-0.02em",
          fontWeight: 800, margin: 0, color: textColor, display: "block",
        }}
      />
      {(title || editable) && (
        <Ed
          tag="div"
          path="title"
          value={title}
          editable={editable}
          onTextEdit={onTextEdit}
          style={{
            color: accent, fontSize: pt(1.05), fontWeight: 700, letterSpacing: "0.06em",
            textTransform: "uppercase", marginTop: "1.5mm",
          }}
        />
      )}
      {(c.email || c.phone || c.location || c.linkedin || c.website) && (
        <div style={{
          display: "flex", flexWrap: "wrap", columnGap: "3mm", rowGap: "1mm",
          marginTop: "2.5mm", color: muted, fontSize: pt(0.85), fontWeight: 500,
        }}>
          {c.email && <ContactItem icon={Mail} pt={pt}>{c.email}</ContactItem>}
          {c.phone && <ContactItem icon={Phone} pt={pt}>{c.phone}</ContactItem>}
          {c.location && <ContactItem icon={MapPin} pt={pt}>{c.location}</ContactItem>}
          {c.linkedin && <ContactItem icon={Linkedin} pt={pt}>{c.linkedin}</ContactItem>}
          {c.website && <ContactItem icon={Globe} pt={pt}>{c.website}</ContactItem>}
        </div>
      )}
    </div>
  );
}

function HeroBanner({ style, bg, accent, logo, photo, company, name, title, contact, pt, fontHeading, editable, onTextEdit }) {
  const c = contact || {};
  const isGradient = style === "gradient";
  const isMinimal = style === "minimal";
  const isAccentBar = style === "accent_bar";
  // Minimal = just a thin accent line under the name (no bg)
  const bgStyle = isMinimal
    ? { background: "#fff", color: "#0F172A" }
    : isGradient
      ? { background: `linear-gradient(135deg, ${bg} 0%, ${darken(bg, 18)} 100%)`, color: "#fff" }
      : isAccentBar
        ? { background: accent, color: "#fff" }
        : { background: bg, color: "#fff" };

  const textOnHero = isMinimal ? "#0F172A" : "#fff";
  const subtle = isMinimal ? "#64748B" : "rgba(255,255,255,0.75)";

  return (
    <div data-testid="resume-hero" style={{
      ...bgStyle,
      padding: "9mm 10mm 8mm 10mm",
      display: "flex",
      alignItems: "center",
      gap: "6mm",
      borderBottom: isMinimal ? `0.5mm solid ${accent}` : "none",
    }}>
      {photo && (
        <div style={{ flexShrink: 0, width: "24mm", height: "24mm", borderRadius: "999px", overflow: "hidden", border: isMinimal ? `0.5mm solid ${accent}` : "0.8mm solid rgba(255,255,255,0.4)", background: "#fff" }}>
          <img src={photo} alt="candidate" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      {logo && !photo && (
        <div style={{ flexShrink: 0, width: "22mm", height: "22mm", borderRadius: "2mm", overflow: "hidden", background: isMinimal ? "#F4F6FA" : "rgba(255,255,255,0.12)", display: "grid", placeItems: "center" }}>
          <img src={logo} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {company && (
          <div style={{ fontSize: pt(0.8), letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, color: subtle, marginBottom: "1.5mm" }}>{company}</div>
        )}
        <Ed
          tag="div"
          path="name"
          value={name}
          editable={editable}
          onTextEdit={onTextEdit}
          style={{
            fontFamily: `"${fontHeading}", sans-serif`,
            fontSize: pt(2.6), fontWeight: 800, letterSpacing: "-0.02em",
            lineHeight: 1.04, color: textOnHero,
          }}
        />
        {(title || editable) && (
          <Ed
            tag="div"
            path="title"
            value={title}
            editable={editable}
            onTextEdit={onTextEdit}
            style={{ marginTop: "1.5mm", fontSize: pt(1.05), fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: isMinimal ? accent : "rgba(255,255,255,0.9)" }}
          />
        )}
        {(c.email || c.phone || c.location || c.linkedin) && (
          <div style={{ marginTop: "2mm", display: "flex", flexWrap: "wrap", columnGap: "3mm", rowGap: "1mm", color: subtle, fontSize: pt(0.85), fontWeight: 500 }}>
            {c.email && <ContactItem icon={Mail} pt={pt}>{c.email}</ContactItem>}
            {c.phone && <ContactItem icon={Phone} pt={pt}>{c.phone}</ContactItem>}
            {c.location && <ContactItem icon={MapPin} pt={pt}>{c.location}</ContactItem>}
            {c.linkedin && <ContactItem icon={Linkedin} pt={pt}>{c.linkedin}</ContactItem>}
          </div>
        )}
      </div>
      {logo && photo && (
        <div style={{ flexShrink: 0, width: "16mm", height: "16mm", borderRadius: "2mm", overflow: "hidden", background: isMinimal ? "#F4F6FA" : "rgba(255,255,255,0.12)", display: "grid", placeItems: "center" }}>
          <img src={logo} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </div>
      )}
    </div>
  );
}

function darken(hex, pct) {
  // Simple HSL-less darken for gradient stop. Returns darker hex.
  const h = hex.replace("#", "");
  const num = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const r = Math.max(0, ((num >> 16) & 255) - Math.round(255 * pct / 100));
  const g = Math.max(0, ((num >> 8) & 255) - Math.round(255 * pct / 100));
  const b = Math.max(0, (num & 255) - Math.round(255 * pct / 100));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

function SidebarBlocks({ s, d, pt, visible, accent }) {
  return (
    <>
      {visible("competencies") && d.competencies?.length > 0 && (
        <SideSection title="Core Competencies" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading} first>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "1mm" }}>
            {d.competencies.map((str, i) => (
              <span key={i} style={{
                padding: "0.5mm 2mm", borderRadius: "999px", background: "#fff",
                border: "1px solid #E2E8F0", color: s.sidebar_text, fontSize: pt(0.85),
                fontWeight: 500,
              }}>{str}</span>
            ))}
          </div>
          {d.section_breaks?.competencies && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
        </SideSection>
      )}

      {visible("certifications") && d.certifications?.length > 0 && (
        <SideSection title="Certifications" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.6mm" }}>
            {d.certifications.map((c2, i) => (
              <div key={i}>
                <div style={AVOID_BREAK}>
                  <div style={{ fontWeight: 700, color: s.sidebar_text, fontSize: pt(0.95) }}>{c2.name}</div>
                  <div style={{ color: s.muted_color, fontSize: pt(0.82) }}>
                    {[c2.issuer, c2.year].filter(Boolean).join("  ·  ")}
                  </div>
                </div>
                {c2.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
              </div>
            ))}
          </div>
          {d.section_breaks?.certifications && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
        </SideSection>
      )}

      {visible("languages") && d.languages?.length > 0 && (
        <SideSection title="Languages" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.4mm" }}>
            {d.languages.map((l, i) => (
              <div key={i}>
                <div style={AVOID_BREAK}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 700, color: s.sidebar_text, fontSize: pt(0.95) }}>{l.name}</span>
                    <span style={{ display: "flex", gap: "0.8mm" }}>
                      {[0, 1, 2, 3, 4].map((n) => (
                        <span key={n} style={{
                          width: "1.4mm", height: "1.4mm", borderRadius: "999px",
                          background: n < (PROF_LEVEL[l.proficiency] || 4) ? accent : "#CBD5E1",
                        }} />
                      ))}
                    </span>
                  </div>
                  <div style={{ color: s.muted_color, fontSize: pt(0.82) }}>{l.proficiency}</div>
                </div>
                {l.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
              </div>
            ))}
          </div>
          {d.section_breaks?.languages && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
        </SideSection>
      )}

      {visible("highlights") && d.highlights?.length > 0 && (
        <SideSection title="Key Highlights" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.6mm" }}>
            {d.highlights.map((h, i) => (
              <div key={i}>
                <div style={{
                  border: `0.3mm solid ${accent}44`, borderRadius: "1.2mm",
                  padding: "1.6mm", background: "#fff", ...AVOID_BREAK,
                }}>
                  <div style={{ fontWeight: 700, color: accent, fontSize: pt(0.92) }}>{h.title}</div>
                  <div style={{ marginTop: "0.4mm", color: s.sidebar_text, fontSize: pt(0.82), lineHeight: 1.4 }}>{h.description}</div>
                </div>
                {h.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
              </div>
            ))}
          </div>
          {d.section_breaks?.highlights && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
        </SideSection>
      )}

      {visible("additional_info") && d.additional_info?.length > 0 && (
        <SideSection title="Additional Info" accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading}>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.4mm" }}>
            {d.additional_info.map((str, i) => (
              <li key={i} style={{ color: s.sidebar_text, fontSize: pt(0.88) }}>• {str}</li>
            ))}
          </ul>
          {d.section_breaks?.additional_info && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
        </SideSection>
      )}

      {/* Custom user-defined sections — RIGHT column */}
      {(d.custom_sections || []).filter((cs) => cs.visible !== false && cs.column === "right").map((cs) => (
        <CustomSectionRender key={cs.id} cs={cs} accent={accent} headerStyle={s.header_style} pt={pt} font={s.font_heading} textColor={s.sidebar_text} muted={s.muted_color} sidebar />
      ))}
    </>
  );
}

function ContactItem({ icon: Icon, pt, children }) {
  const sizeMm = 3.0; // ~8.5pt size
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "1mm" }}>
      <Icon style={{ width: `${sizeMm}mm`, height: `${sizeMm}mm` }} />
      {children}
    </span>
  );
}

function Section({ title, accent, headerStyle, pt, font, children }) {
  return (
    <div style={{ marginTop: "4.5mm", ...AVOID_BREAK }}>
      <SectionHeader title={title} accent={accent} headerStyle={headerStyle} pt={pt} font={font} />
      {children}
    </div>
  );
}

function SideSection({ title, accent, headerStyle, pt, font, first, children }) {
  return (
    <div style={{ marginTop: first ? 0 : "4.5mm", ...AVOID_BREAK }}>
      <SectionHeader title={title} accent={accent} headerStyle={headerStyle} pt={pt} font={font} compact />
      {children}
    </div>
  );
}

function SectionHeader({ title, accent, headerStyle = "underline", pt, font = "Manrope", compact }) {
  const sz = compact ? pt(0.85) : pt(0.95);
  const baseStyle = {
    color: accent,
    fontSize: sz,
    fontFamily: `"${font}", sans-serif`,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.18em",
  };
  if (headerStyle === "block") {
    return (
      <div style={{ marginBottom: "2mm", display: "inline-block", padding: "0.6mm 2mm", borderRadius: "1mm", background: accent }}>
        <span style={{ ...baseStyle, color: "#fff" }}>{title}</span>
      </div>
    );
  }
  if (headerStyle === "minimal") {
    return <div style={{ ...baseStyle, marginBottom: "2mm" }}>{title}</div>;
  }
  return (
    <div style={{ marginBottom: "2mm" }}>
      <div style={{ ...baseStyle, marginBottom: "1mm" }}>{title}</div>
      <div style={{ height: "0.4mm", borderRadius: "999px", background: accent }} />
    </div>
  );
}


function CustomSectionRender({ cs, accent, headerStyle, pt, font, textColor, muted, sidebar }) {
  const items = Array.isArray(cs.items) ? cs.items : [];
  const Wrapper = sidebar ? SideSection : Section;
  return (
    <Wrapper title={cs.title || "Custom"} accent={accent} headerStyle={headerStyle} pt={pt} font={font}>
      {cs.type === "text" && cs.content_text && (
        <p style={{ color: textColor, fontSize: pt(sidebar ? 0.9 : 0.98), margin: 0, whiteSpace: "pre-wrap" }}>{cs.content_text}</p>
      )}
      {cs.type === "bullets" && items.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.8mm" }}>
          {items.map((it, i) => (
            <li key={i} style={{ display: "flex", gap: "1.6mm", color: textColor, fontSize: pt(sidebar ? 0.88 : 0.97), lineHeight: 1.4, ...AVOID_BREAK }}>
              <span style={{ flexShrink: 0, marginTop: "1.4mm", width: "1mm", height: "1mm", borderRadius: "999px", background: accent }} />
              <span>{it.text || it.title}</span>
            </li>
          ))}
        </ul>
      )}
      {cs.type === "timeline" && items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2mm" }}>
          {items.map((it, i) => (
            <div key={i} style={AVOID_BREAK}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                <div style={{ fontWeight: 700, color: textColor, fontSize: pt(sidebar ? 0.95 : 1.08) }}>{it.title}</div>
                <div style={{ fontStyle: "italic", color: muted, fontSize: pt(0.85) }}>{it.date}</div>
              </div>
              {it.subtitle && <div style={{ color: muted, fontSize: pt(0.92) }}>{it.subtitle}</div>}
              {it.description && <div style={{ marginTop: "0.4mm", color: textColor, fontSize: pt(0.95) }}>{it.description}</div>}
            </div>
          ))}
        </div>
      )}
      {cs.type === "cards" && items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "1.6mm" }}>
          {items.map((it, i) => (
            <div key={i} style={{ border: `0.3mm solid ${accent}44`, borderRadius: "1.2mm", padding: "1.6mm", background: "#fff", ...AVOID_BREAK }}>
              <div style={{ fontWeight: 700, color: accent, fontSize: pt(0.92) }}>{it.title}</div>
              {it.description && <div style={{ marginTop: "0.4mm", color: textColor, fontSize: pt(0.82), lineHeight: 1.4 }}>{it.description}</div>}
            </div>
          ))}
        </div>
      )}
      {cs.page_break_after && <div aria-hidden="true" data-page-break style={{ pageBreakAfter: "always", breakAfter: "page", height: 0 }} />}
    </Wrapper>
  );
}
